import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Sheet, SheetContent, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { emrService, EmrClinicalNote, EmrEncounter } from '@/services/emrService';
import { Loader2, X, Sparkles, Video, Lock, CheckCircle2, LinkIcon } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { AnimatePresence, motion } from 'framer-motion';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';
import {
  aiRecommendationService,
  parseRecommendation,
} from '@/services/aiRecommendationService';
import {
  isStructuredRecommendation,
  isLabRecommendation,
} from '@/types/intellihealthStructured';
import { useQuery } from '@tanstack/react-query';

// Subset of OFF_ENCOUNTER_REASONS the backend accepts (see
// novamed-api/src/middleware/clinicalContext.js) that make sense for
// provider-authored notes from a patient page.
const OFF_ENCOUNTER_NOTE_REASONS: Array<{ value: string; label: string }> = [
  { value: 'chart_correction', label: 'Chart correction' },
  { value: 'letter', label: 'Letter / correspondence' },
  { value: 'pre_visit_task', label: 'Pre-visit task' },
  { value: 'refill_request', label: 'Refill request' },
];

/**
 * SOAP note text validation.
 * Clinical notation requires special characters (<, >, %, °, #, +, etc.)
 * so we validate only length, not character content.
 * XSS protection is handled at render-time — React JSX auto-escapes all
 * interpolated text, and SOAP notes are never rendered via dangerouslySetInnerHTML.
 */
const noteTextSchema = (fieldName: string) =>
  z.string()
    .max(5000, { message: `${fieldName} cannot exceed 5000 characters` });

const noteFormSchema = z.object({
  subjective: noteTextSchema('Subjective'),
  objective: noteTextSchema('Objective'),
  assessment: noteTextSchema('Assessment'),
  plan: noteTextSchema('Plan'),
  body: noteTextSchema('Note'),
});

type NoteFormData = z.infer<typeof noteFormSchema>;

// Reason → backend note_type. Only used for off-encounter notes; SOAP
// notes (encounter-linked) keep note_type='soap'.
const REASON_TO_NOTE_TYPE: Record<
  string,
  'chart_note' | 'progress' | 'other'
> = {
  chart_correction: 'chart_note',
  letter: 'other',
  pre_visit_task: 'progress',
  refill_request: 'progress',
};

interface NoteFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  note: EmrClinicalNote | null;
  onSubmit: () => void;
  // Optional: for AI-assisted generation from transcripts
  transcriptId?: string;
  appointmentId?: string;
  encounterId?: string;
  /**
   * When true and `encounterId` is set, auto-trigger SOAP generation as soon
   * as the drawer opens. Used by the "Draft with IntelliHealth" CTA on the
   * cockpit Notes empty state.
   */
  autoGenerateOnOpen?: boolean;
}

/** Dropdown row label: "May 21, 2026, 9:00 AM" — full year + minute. */
const fullEncounterLabel = (e: EmrEncounter) =>
  new Date(e.start_time).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/**
 * Trigger summary: "May 21, 9:00 AM · In progress". Drops year and chief
 * complaint to fit the single-row trigger height without wrapping.
 */
const compactEncounterLabel = (e: EmrEncounter | undefined) => {
  if (!e) return '';
  const dt = new Date(e.start_time).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const status = e.status
    .replace('_', ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
  return `${dt} · ${status}`;
};

const NoteFormDrawer = ({
  open,
  onOpenChange,
  patientId,
  onSubmit,
  note,
  transcriptId,
  appointmentId,
  encounterId,
  autoGenerateOnOpen,
}: NoteFormDrawerProps) => {
  const { clinic } = useClinic();
  const { user } = useAuth();

  const form = useForm<NoteFormData>({
    resolver: zodResolver(noteFormSchema),
    defaultValues: {
      subjective: '',
      objective: '',
      assessment: '',
      plan: '',
      body: '',
    },
  });

  const { handleSubmit: handleSubmitForm, formState: { errors } } = form;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasTranscript, setHasTranscript] = useState(false);

  // Encounter linkage — only relevant when creating a new note from a context
  // that doesn't already supply an encounter (e.g., the patient detail page).
  // The cockpit passes encounterId in props, so the picker is hidden there.
  const isEditing = !!note;
  const showEncounterPicker = !isEditing && !encounterId;
  const [linkType, setLinkType] = useState<'encounter' | 'off'>('encounter');
  const [selectedEncounterId, setSelectedEncounterId] = useState<string>('');
  const [offReason, setOffReason] = useState<string>('chart_correction');

  // SOAP-shaped form vs. simple-body form. SOAP belongs inside an encounter
  // (or when editing an existing SOAP note); off-encounter notes get a
  // single narrative textarea with a note_type that matches the reason.
  const isSoapShape =
    isEditing
      ? (note?.note_type ?? 'soap') === 'soap'
      : !showEncounterPicker || linkType === 'encounter';

  const { data: patientEncounters = [] } = useQuery<EmrEncounter[]>({
    queryKey: ['note-form-encounters', clinic?.id, patientId],
    queryFn: async () => {
      if (!clinic?.id || !patientId) return [] as EmrEncounter[];
      try {
        return await emrService.getPatientEncounters(clinic.id, patientId);
      } catch {
        return [] as EmrEncounter[];
      }
    },
    enabled: open && showEncounterPicker && !!clinic?.id && !!patientId,
    staleTime: 60_000,
  });

  // Sort newest first; only encounters that can host a write
  // ('planned' / 'in_progress') are valid targets — completed/signed
  // encounters are read-only. Show recent ones too for visibility but
  // disable the action if not writable.
  const sortedEncounters = useMemo(() => {
    return [...patientEncounters].sort(
      (a, b) =>
        new Date(b.start_time).getTime() - new Date(a.start_time).getTime(),
    );
  }, [patientEncounters]);

  // Auto-select the most recent in-progress encounter as the default.
  useEffect(() => {
    if (!showEncounterPicker) return;
    if (selectedEncounterId) return;
    const inProgress = sortedEncounters.find(
      (e) => e.status === 'in_progress' || e.status === 'scheduled',
    );
    if (inProgress) {
      setLinkType('encounter');
      setSelectedEncounterId(inProgress.id);
    } else if (sortedEncounters.length === 0) {
      // No encounters at all — default to off-encounter so the user has a
      // valid path forward without a phantom selector.
      setLinkType('off');
    }
  }, [sortedEncounters, showEncounterPicker, selectedEncounterId]);
  const [transcriptInfo, setTranscriptInfo] = useState<{
    id: string;
    status: string;
    duration_seconds?: number;
  } | null>(null);
  // Auto-save: only kicks in for existing (already-saved) notes that aren't
  // signed/locked. Avoids round-tripping a not-yet-created draft.
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const lastSavedRef = useRef<string>('');
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check for available transcript when component mounts
  useEffect(() => {
    const checkForTranscript = async () => {
      if (!clinic?.id) return;
      
      // Check by transcriptId first
      if (transcriptId) {
        try {
          const response = await api.get(`/emr/${clinic.id}/transcripts/${transcriptId}`);
          if (response.data && response.data.status === 'completed') {
            setHasTranscript(true);
            setTranscriptInfo({
              id: response.data.id,
              status: response.data.status,
              duration_seconds: response.data.duration_seconds
            });
          }
        } catch (_error) { // Transcript not found — expected for new notes
        }
        return;
      }
      
      // Check by appointmentId
      if (appointmentId) {
        try {
          const response = await api.get(`/emr/${clinic.id}/appointments/${appointmentId}/transcript`);
          if (response.data && response.data.status === 'completed') {
            setHasTranscript(true);
            setTranscriptInfo({
              id: response.data.id,
              status: response.data.status,
              duration_seconds: response.data.duration_seconds
            });
          }
        } catch (_error) { // Transcript not found — expected for new notes
        }
      }
    };

    if (open) {
      checkForTranscript();
    }
  }, [open, clinic?.id, transcriptId, appointmentId]);

  // Generate SOAP from transcript using AI
  const handleGenerateFromTranscript = async () => {
    if (!transcriptInfo?.id || !clinic?.id) return;

    setIsGenerating(true);
    try {
      const response = await api.post(
        `/emr/${clinic.id}/transcripts/${transcriptInfo.id}/generate-soap`
      );

      if (response.data.success && response.data.note) {
        const generatedNote = response.data.note;
        
        // Fill the form with generated content
        form.setValue('subjective', generatedNote.subjective || '', { shouldValidate: true });
        form.setValue('objective', generatedNote.objective || '', { shouldValidate: true });
        form.setValue('assessment', generatedNote.assessment || '', { shouldValidate: true });
        form.setValue('plan', generatedNote.plan || '', { shouldValidate: true });
        
        toast({
          title: 'SOAP Note Generated',
          description: 'AI has pre-filled the form. Please review and edit as needed.',
          variant: 'success',
        });
      }
    } catch (error: any) {
      console.error('Error generating SOAP from transcript:', error);
      const apiError = error.response?.data?.error;
      const message = apiError?.message || error.response?.data?.message || error.message || 'Failed to generate SOAP note';
      if (apiError?.code === 'AI_UNAVAILABLE') {
        toast({
          title: 'AI Drafting Unavailable',
          description: message,
          variant: 'default',
        });
      } else {
        toast({
          title: 'Generation Failed',
          description: message,
          variant: 'destructive',
        });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Auto-fill the form from the patient's latest IntelliHealth recommendation.
  // Works for both encounter-linked SOAP (S/O/A/P pre-filled from the
  // structured recommendation's `soap` block) and off-encounter narrative
  // notes (single body pre-filled from the recommendation's assessment /
  // summary). Transcript-based generation is still preferred when a
  // telehealth transcript is available.
  const handleGenerateFromIntelliHealth = async () => {
    if (!patientId || !clinic?.id) return;
    setIsGenerating(true);
    try {
      const recs = await aiRecommendationService.getPatientRecommendations(
        patientId,
        1,
      );
      const parsed = parseRecommendation(recs.data?.recommendation);

      // Try structured recommendation first
      let s: string | undefined;
      let o: string | undefined;
      let a: string | undefined;
      let p: string | undefined;
      let summaryFallback: string | undefined;

      if (
        parsed
        && typeof parsed === 'object'
        && isStructuredRecommendation(parsed)
        && isLabRecommendation(parsed)
      ) {
        s = parsed.soap?.subjective || undefined;
        o = parsed.soap?.objective || undefined;
        a = parsed.soap?.assessment || undefined;
        p = parsed.soap?.plan || undefined;
        summaryFallback = parsed.summary || undefined;
      } else if (typeof parsed === 'string') {
        const split = splitMarkdownSoap(parsed);
        s = split.subjective;
        o = split.objective;
        a = split.assessment;
        p = split.plan;
        summaryFallback = parsed;
      }

      const hasAny = s || o || a || p || summaryFallback;
      if (!hasAny) {
        toast({
          title: 'No recommendation available',
          description:
            "IntelliHealth hasn't produced a recommendation for this patient yet. Generate one from the Summary tab first.",
          variant: 'default',
        });
        return;
      }

      if (isSoapShape) {
        if (s) form.setValue('subjective', s, { shouldValidate: true });
        if (o) form.setValue('objective', o, { shouldValidate: true });
        if (a) form.setValue('assessment', a, { shouldValidate: true });
        if (p) form.setValue('plan', p, { shouldValidate: true });
      } else {
        // Off-encounter narrative — collapse SOAP into a single body, or
        // fall back to the recommendation summary.
        const composed = [
          s ? `S: ${s}` : '',
          o ? `O: ${o}` : '',
          a ? `A: ${a}` : '',
          p ? `P: ${p}` : '',
        ]
          .filter(Boolean)
          .join('\n\n');
        const body = composed || summaryFallback || '';
        if (body) form.setValue('body', body, { shouldValidate: true });
      }

      toast({
        title: 'Pre-filled from IntelliHealth',
        description:
          'Review and edit before signing. IntelliHealth output is advisory.',
        variant: 'success',
      });
    } catch (error: any) {
      console.error('Error generating from IntelliHealth:', error);
      const apiError = error.response?.data?.error;
      const message =
        apiError?.message
        || error.response?.data?.message
        || error.message
        || 'Failed to fetch IntelliHealth recommendation';
      toast({
        title: 'Generation Failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Lightweight S/O/A/P parser for plain markdown recommendations.
  // Mirrors the helper used elsewhere; kept local so this drawer doesn't
  // depend on the (now-removed) SoapDraftDialog file.
  function splitMarkdownSoap(markdown: string): {
    subjective?: string;
    objective?: string;
    assessment?: string;
    plan?: string;
  } {
    const out: {
      subjective?: string;
      objective?: string;
      assessment?: string;
      plan?: string;
    } = {};
    const blocks = markdown.split(
      /\n(?=#{1,6}\s|\bS:|\bO:|\bA:|\bP:|\bSubjective\b|\bObjective\b|\bAssessment\b|\bPlan\b)/i,
    );
    for (const block of blocks) {
      const head = block.match(
        /^(?:#{1,6}\s+)?(Subjective|Objective|Assessment|Plan|S|O|A|P)[\s:.\-–]+/i,
      );
      if (!head) continue;
      const key = head[1][0].toLowerCase();
      const body = block.slice(head[0].length).trim();
      if (!body) continue;
      if (key === 's') out.subjective = body;
      else if (key === 'o') out.objective = body;
      else if (key === 'a') out.assessment = body;
      else if (key === 'p') out.plan = body;
    }
    return out;
  }

  // Auto-trigger AI fill on first open when a caller passes
  // `autoGenerateOnOpen`. Only runs for new (note === null) drafts; never
  // re-fires on subsequent open/close cycles in the same drawer instance.
  const autoGeneratedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      autoGeneratedRef.current = false;
      return;
    }
    if (note) return;
    if (!autoGenerateOnOpen) return;
    if (!patientId || !clinic?.id) return;
    if (autoGeneratedRef.current) return;
    autoGeneratedRef.current = true;
    handleGenerateFromIntelliHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoGenerateOnOpen, note, patientId, clinic?.id]);

  const handleSubmit = handleSubmitForm(async (data: NoteFormData) => {
    const fieldsForShape = isSoapShape
      ? [data.subjective, data.objective, data.assessment, data.plan]
      : [data.body];
    const hasAtLeastOneField = fieldsForShape.some(
      (value) => value.trim().length > 0,
    );

    if (!hasAtLeastOneField) {
      toast({
        title: 'Validation Error',
        description: isSoapShape
          ? 'Please fill at least one SOAP field to save the note.'
          : 'Please write something in the note body before saving.',
        variant: 'destructive',
      });
      return;
    }

    if (!clinic?.id) {
      toast({
        title: 'Error',
        description: 'Clinic context not available',
        variant: 'destructive',
      });
      return;
    }

    // If the user is creating a new note from a context without a preset
    // encounter and chose "Existing encounter" but didn't pick one, block
    // submit before hitting the backend.
    if (
      showEncounterPicker
      && linkType === 'encounter'
      && !selectedEncounterId
    ) {
      toast({
        title: 'Pick an encounter',
        description:
          'Select an encounter to link this note to, or switch to off-encounter and choose a reason.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      if (note) {
        await emrService.updateNote(clinic.id, note.id, {
          subjective: data.subjective,
          objective: data.objective,
          assessment: data.assessment,
          plan: data.plan,
        });
        toast({
          title: 'Success',
          description: 'SOAP note updated successfully',
          variant: 'success',
        });
      } else {
        // When writing outside an open encounter (chart correction, letter, etc.)
        // the backend requires an explicit off_encounter_reason. For the main
        // in-visit flow both encounter_id and appointment_id are present.
        // SOAP notes carry subjective/objective/assessment/plan; off-encounter
        // narrative notes (chart_note / progress / other) collapse to a single
        // body and are stored under `assessment` since the schema has no
        // dedicated body column.
        const noteType: EmrClinicalNote['note_type'] = isSoapShape
          ? 'soap'
          : REASON_TO_NOTE_TYPE[offReason] || 'other';

        const payload: Parameters<typeof emrService.createNote>[1] = {
          patient_id: patientId,
          // Send the auth user's practitioner_id when known. The backend
          // also falls back to the auth user's record, and if both are
          // empty it now resolves to the patient's primary practitioner.
          practitioner_id: user?.practitionerId || '',
          note_type: noteType,
          note_date: new Date().toISOString(),
          subjective: isSoapShape ? data.subjective || undefined : undefined,
          objective: isSoapShape ? data.objective || undefined : undefined,
          assessment: isSoapShape
            ? data.assessment || undefined
            : data.body || undefined,
          plan: isSoapShape ? data.plan || undefined : undefined,
          is_signed: false,
          is_addendum: false,
          is_locked: false,
        };
        // Resolve linkage. Priority:
        //   1. encounterId / appointmentId passed in props (cockpit context)
        //   2. user's pick from the encounter picker (patient page)
        //   3. user's off-encounter reason from the picker
        const resolvedEncounterId =
          encounterId ||
          (showEncounterPicker && linkType === 'encounter'
            ? selectedEncounterId
            : '');
        const resolvedOffReason =
          showEncounterPicker && linkType === 'off' ? offReason : '';

        if (resolvedEncounterId) payload.encounter_id = resolvedEncounterId;
        if (appointmentId) payload.appointment_id = appointmentId;
        if (!resolvedEncounterId) {
          // Fall through to chart_correction only if nothing was picked at all
          // (e.g., callers that bypass the picker). Otherwise use the user
          // selection so the audit trail records the actual reason.
          (payload as any).off_encounter_reason =
            resolvedOffReason || 'chart_correction';
        }

        await emrService.createNote(clinic.id, payload);

        toast({
          title: 'Success',
          description: 'SOAP note created successfully',
          variant: 'success',
        });
      }
      form.reset();
      onSubmit();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error saving SOAP note:', error);
      const code = error.response?.data?.error?.code;
      const message = code === 'NOTE_LOCKED'
        ? 'This note is signed and locked. Create an addendum instead.'
        : error.response?.data?.error?.message || error.message || 'Failed to save SOAP note';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleSignAndLock = async () => {
    if (!note || !clinic?.id) return;
    setIsSigning(true);
    try {
      await emrService.signNote(clinic.id, note.id);
      toast({
        title: 'Note Signed',
        description: 'The note is now signed and locked. Edits require an addendum.',
        variant: 'success',
      });
      onSubmit();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Sign Failed',
        description: error.response?.data?.error?.message || error.message || 'Unable to sign note',
        variant: 'destructive',
      });
    } finally {
      setIsSigning(false);
    }
  };

  useEffect(() => {
    if (note) {
      form.reset({
        subjective: note.subjective || '',
        objective: note.objective || '',
        assessment: note.assessment || '',
        plan: note.plan || '',
      });
      lastSavedRef.current = JSON.stringify({
        s: note.subjective || '',
        o: note.objective || '',
        a: note.assessment || '',
        p: note.plan || '',
      });
    }
  }, [note]);

  // Auto-save: 10s debounce on edits to an existing unsigned note.
  useEffect(() => {
    if (!note?.id || !clinic?.id) return;
    if (note.is_signed || note.is_locked) return;

    const sub = form.watch((values) => {
      const snapshot = JSON.stringify({
        s: values.subjective || '',
        o: values.objective || '',
        a: values.assessment || '',
        p: values.plan || '',
      });
      if (snapshot === lastSavedRef.current) return;
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(async () => {
        setAutoSaveStatus('saving');
        try {
          await emrService.updateNote(clinic.id, note.id, {
            subjective: values.subjective,
            objective: values.objective,
            assessment: values.assessment,
            plan: values.plan,
          });
          lastSavedRef.current = snapshot;
          setAutoSaveStatus('saved');
        } catch (err) {
          setAutoSaveStatus('error');
        }
      }, 10000);
    });
    return () => {
      sub.unsubscribe();
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note?.id, note?.is_signed, note?.is_locked, clinic?.id]);

  const isEdit = !!note;
  const isLocked = !!(note?.is_locked || note?.is_signed);
  const canSign = !!note && !isLocked;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full !max-w-[540px] rounded-l-2xl p-0 flex flex-col gap-0 [&>button]:hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <SheetTitle className="font-semibold text-slate-900" style={{ fontSize: '1.05rem' }}>
              {isLocked ? 'View' : isEdit ? 'Edit' : 'Create'} SOAP Note
            </SheetTitle>
            {isLocked && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                <Lock className="h-3 w-3" />
                Signed &amp; locked
              </span>
            )}
            {!isLocked && note && autoSaveStatus === 'saving' && (
              <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin" /> Saving…
              </span>
            )}
            {!isLocked && note && autoSaveStatus === 'saved' && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                <CheckCircle2 className="h-3 w-3" /> Saved
              </span>
            )}
          </div>
          <SheetClose asChild>
            <button className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors">
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </SheetClose>
        </div>

        {/* AI Assist Banner — only inside an encounter (the visit cockpit).
            On the patient-detail page (no encounter in scope) notes are
            typically administrative / off-encounter and don't need AI
            drafting. Transcript-based generation takes precedence when a
            telehealth transcript is on file. */}
        {!note && encounterId && (
          <div className="px-6 py-3 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-blue-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    AI-Assisted Documentation
                  </p>
                  <p className="text-xs text-slate-500">
                    {hasTranscript
                      ? `Generate from telehealth transcript${transcriptInfo?.duration_seconds ? ` (${Math.floor(transcriptInfo.duration_seconds / 60)}min call)` : ''}`
                      : 'Pre-fill from the latest IntelliHealth recommendation'}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={hasTranscript ? handleGenerateFromTranscript : handleGenerateFromIntelliHealth}
                disabled={isGenerating || isSubmitting}
                className="border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    {hasTranscript ? (
                      <Video className="h-4 w-4 mr-1.5" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-1.5" />
                    )}
                    {hasTranscript ? 'Auto-Fill from Transcript' : 'Fill from IntelliHealth'}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <form id="soap-note-form" onSubmit={handleSubmit} className="space-y-6">
            {showEncounterPicker && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-slate-500" />
                  <h3 className="text-sm font-semibold text-slate-900">
                    Link this note
                  </h3>
                </div>
                <p className="text-xs text-slate-500">
                  Notes are typically tied to a clinical encounter. If this note
                  isn't part of an active visit, pick the off-encounter reason
                  so the audit trail is accurate.
                </p>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setLinkType('encounter')}
                    disabled={sortedEncounters.length === 0 || isLocked}
                    className={`flex-1 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
                      linkType === 'encounter'
                        ? 'border-teal-600 bg-teal-50 text-teal-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50'
                    }`}
                  >
                    Existing encounter
                    {sortedEncounters.length === 0 && (
                      <span className='ml-1 text-[10px] text-slate-400'>(none on file)</span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLinkType('off')}
                    disabled={isLocked}
                    className={`flex-1 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
                      linkType === 'off'
                        ? 'border-teal-600 bg-teal-50 text-teal-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Off-encounter
                  </button>
                </div>

                {linkType === 'encounter' && sortedEncounters.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Select encounter</Label>
                    <Select
                      value={selectedEncounterId}
                      onValueChange={setSelectedEncounterId}
                      disabled={isLocked}
                    >
                      {/* Override SelectValue so the trigger shows a compact
                          summary (date · status). Without this, Radix mirrors
                          the SelectItem's children verbatim and the trigger
                          inherits the dropdown's stacked layout, which
                          overflows the trigger row. */}
                      <SelectTrigger className="h-9 text-sm">
                        {selectedEncounterId
                          ? (
                            <span className="truncate text-left">
                              {compactEncounterLabel(
                                sortedEncounters.find(
                                  (e) => e.id === selectedEncounterId,
                                ),
                              )}
                            </span>
                          )
                          : <SelectValue placeholder="Choose an encounter…" />}
                      </SelectTrigger>
                      <SelectContent>
                        {sortedEncounters.map((e) => {
                          const writable =
                            e.status === 'in_progress' || e.status === 'scheduled';
                          return (
                            <SelectItem
                              key={e.id}
                              value={e.id}
                              disabled={!writable}
                              className="py-2"
                            >
                              <div className="flex flex-col gap-0.5 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium">
                                    {fullEncounterLabel(e)}
                                  </span>
                                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                                    {e.status.replace('_', ' ')}
                                  </Badge>
                                </div>
                                {e.chief_complaint && (
                                  <p className="truncate max-w-[260px] text-xs text-slate-500">
                                    {e.chief_complaint}
                                  </p>
                                )}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-400">
                      Only in-progress or scheduled encounters accept new notes.
                    </p>
                  </div>
                )}

                {linkType === 'off' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Reason</Label>
                    <Select
                      value={offReason}
                      onValueChange={setOffReason}
                      disabled={isLocked}
                    >
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OFF_ENCOUNTER_NOTE_REASONS.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {isSoapShape ? (
              <>
                {/* Subjective */}
                <div className="space-y-4">
                  <h3 className="uppercase text-[0.8rem] font-semibold text-[#60378B]">Subjective</h3>
                  <div className="space-y-1.5">
                    <Label htmlFor="subjective">
                      Patient's Description
                    </Label>
                    <Textarea
                      id="subjective"
                      value={form.watch('subjective')}
                      onChange={(e) => form.setValue('subjective', e.target.value.trimStart(), { shouldValidate: true })}
                      placeholder="Patient's description of symptoms, concerns, or complaints..."
                      className="min-h-[100px]"
                      disabled={isSubmitting || isLocked}
                    />
                    <ErrorMessage error={errors.subjective} />
                  </div>
                </div>

                {/* Objective */}
                <div className="space-y-4">
                  <h3 className="uppercase text-[0.8rem] font-semibold text-[#60378B]">Objective</h3>
                  <div className="space-y-1.5">
                    <Label htmlFor="objective">
                      Clinical Findings
                    </Label>
                    <Textarea
                      id="objective"
                      value={form.watch('objective')}
                      onChange={(e) => form.setValue('objective', e.target.value.trimStart(), { shouldValidate: true })}
                      placeholder="Observable findings, vital signs, test results..."
                      className="min-h-[100px]"
                      disabled={isSubmitting || isLocked}
                    />
                    <ErrorMessage error={errors.objective} />
                  </div>
                </div>

                {/* Assessment */}
                <div className="space-y-4">
                  <h3 className="uppercase text-[0.8rem] font-semibold text-[#60378B]">Assessment</h3>
                  <div className="space-y-1.5">
                    <Label htmlFor="assessment">
                      Clinical Judgment
                    </Label>
                    <Textarea
                      id="assessment"
                      value={form.watch('assessment')}
                      onChange={(e) => form.setValue('assessment', e.target.value.trimStart(), { shouldValidate: true })}
                      placeholder="Clinical judgment, diagnosis, or interpretation..."
                      className="min-h-[100px]"
                      disabled={isSubmitting || isLocked}
                    />
                    <ErrorMessage error={errors.assessment} />
                  </div>
                </div>

                {/* Plan */}
                <div className="space-y-4">
                  <h3 className="uppercase text-[0.8rem] font-semibold text-[#60378B]">Plan</h3>
                  <div className="space-y-1.5">
                    <Label htmlFor="plan">
                      Treatment Plan
                    </Label>
                    <Textarea
                      id="plan"
                      value={form.watch('plan')}
                      onChange={(e) => form.setValue('plan', e.target.value.trimStart(), { shouldValidate: true })}
                      placeholder="Treatment plan, follow-up instructions, next steps..."
                      className="min-h-[100px]"
                      disabled={isSubmitting || isLocked}
                    />
                    <ErrorMessage error={errors.plan} />
                  </div>
                </div>
              </>
            ) : (
              /* Off-encounter narrative — single body, note_type derived from reason */
              <div className="space-y-4">
                <h3 className="uppercase text-[0.8rem] font-semibold text-[#60378B]">
                  {OFF_ENCOUNTER_NOTE_REASONS.find((r) => r.value === offReason)?.label || 'Note'}
                </h3>
                <div className="space-y-1.5">
                  <Label htmlFor="body">Note body</Label>
                  <Textarea
                    id="body"
                    value={form.watch('body')}
                    onChange={(e) =>
                      form.setValue('body', e.target.value.trimStart(), { shouldValidate: true })
                    }
                    placeholder={
                      offReason === 'chart_correction'
                        ? 'Describe the correction (what changed, why, and from when)…'
                        : offReason === 'letter'
                        ? 'Letter content…'
                        : offReason === 'pre_visit_task'
                        ? 'Notes for the upcoming visit…'
                        : offReason === 'refill_request'
                        ? 'Medication, indication, and refill plan…'
                        : 'Type the note…'
                    }
                    className="min-h-[200px]"
                    disabled={isSubmitting || isLocked}
                  />
                  <ErrorMessage error={errors.body} />
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <Button
            variant="ghost"
            type="button"
            className="text-slate-700 hover:text-slate-900 hover:bg-transparent font-medium"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting || isSigning}
          >
            {isLocked ? 'Close' : 'Cancel'}
          </Button>
          <div className="flex items-center gap-2">
            {canSign && (
              <Button
                type="button"
                variant="outline"
                onClick={handleSignAndLock}
                className="rounded-lg h-11 px-5 border-brand-teal text-brand-teal hover:bg-brand-teal/5"
                disabled={isSubmitting || isSigning}
              >
                {isSigning ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Signing...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <Lock className="w-4 h-4" />
                    <span>Sign &amp; Lock</span>
                  </div>
                )}
              </Button>
            )}
            {!isLocked && (
              <Button
                type="submit"
                form="soap-note-form"
                className="bg-brand-teal hover:bg-brand-teal/90 rounded-lg h-11 px-8"
                disabled={isSubmitting || isSigning}
              >
                {isSubmitting ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{isEdit ? 'Updating...' : 'Creating...'}</span>
                  </div>
                ) : (
                  <span>{isEdit ? 'Update Note' : 'Create Note'}</span>
                )}
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default NoteFormDrawer;


const ErrorMessage = ({ error }: { error: any }) => {
  return (
    <AnimatePresence>
      {error && (
        <motion.p 
          initial={{ opacity: 0, height: 0 }} 
          animate={{ opacity: 1, height: 'auto' }} 
          exit={{ opacity: 0, height: 0 }} 
          className="text-[0.8rem] text-red-500"
        >
          {error.message}
        </motion.p>
      )}
    </AnimatePresence>
  );
};
